import isEmpty from 'lodash/isEmpty'
import { IUserDetails } from 'types'
import { identifyUserDDR, trackDDREvent } from 'utils/tools/datadog'

// @ts-ignore
const doNotTrack = typeof Cypress !== 'undefined' || !__PROD__

/**
 * FP: Fleet Planner
 * TMS: TMS
 * MP: Marketplace
 */
export type TAnalyticsAppName = 'FMS' | 'TMS' | 'MP'

interface IUseAnalyticsReturn<E> {
  appName: TAnalyticsAppName
  appEvents: E
  trackEvent: <F extends keyof E>(
    feature: F,
    event: keyof E[F],
    properties?: Record<string, any>
  ) => void
  identifyUser: (userDetails: IUserDetails | null) => void
}

interface IUseAnalyticsProps<F extends Record<string, Record<string, string>>> {
  appName: TAnalyticsAppName
  appEvents: F
}

/**
 * Handles logic around analytics events tracking
 * WARNING: You should not really need to use this hook in your application.
 * Check for abstraction such as useFMSAnalytics which will provide the right interface for you.
 */
export const useAnalytics = <E extends Record<string, Record<string, string>>>({
  appEvents,
  appName,
}: IUseAnalyticsProps<E>): IUseAnalyticsReturn<E> => {
  /**
   * Handles sending the event to the different analytics third parties
   * @example trackEvent('My Feature', 'Crazy Stuff The User Did', { meta: 'BAR' })
   */
  const trackEvent = <F extends keyof E>(
    feature: F,
    event: keyof E[F],
    properties?: Record<string, any>
  ) => {
    const eventValue = `${appName} > ${String(feature)} > ${appEvents[feature][event]}`

    if (!doNotTrack) {
      trackDDREvent(appName, eventValue, properties) // Datadog tracking
    } else {
      // eslint-disable-next-line no-console
      console.info('[TFX Analytics] ', eventValue, properties)
    }
  }

  /**
   * Handles identifying the user on the different analytics third parties
   * @example identifyUser('ekfjrir23', { lastName: 'Bart', ... })
   */
  const identifyUser = (userDetails: IUserDetails | null) => {
    if (doNotTrack) return
    if (userDetails && !isEmpty(userDetails)) {
      identifyUserDDR(userDetails)
    }
  }

  return {
    appName,
    appEvents,
    trackEvent,
    identifyUser,
  }
}
